import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import AddIcon from '@mui/icons-material/Add';
import ForestIcon from '@mui/icons-material/Forest';
import SearchIcon from '@mui/icons-material/Search';
import {
  useTenantVegetationTypes,
  useCreateTenantVegetationType,
  useUpdateTenantVegetationType,
  useDeleteTenantVegetationType,
  useImportTenantVegetationTypes,
} from '../api/tenantVegetationType';
import { TenantVegetationType } from '../types/tenantVegetationType';
import VegetationTypeTable from '../components/vegetationManagement/VegetationTypeTable';
import VegetationTypeFormDialog from '../components/vegetationManagement/VegetationTypeFormDialog';

interface SnackState {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

export default function VegetationManagementPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TenantVegetationType | null>(null);
  const [snack, setSnack] = useState<SnackState>({ open: false, message: '', severity: 'success' });
  const [search, setSearch] = useState('');

  const { data, isLoading } = useTenantVegetationTypes();
  const allRows: TenantVegetationType[] = (data as any)?.data ?? [];
  const rows = search
    ? allRows.filter((r) => r.veg_class.toLowerCase().includes(search.toLowerCase()))
    : allRows;

  const createMutation = useCreateTenantVegetationType();
  const updateMutation = useUpdateTenantVegetationType();
  const deleteMutation = useDeleteTenantVegetationType();
  const importMutation = useImportTenantVegetationTypes();

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const showSnack = (message: string, severity: 'success' | 'error') => {
    setSnack({ open: true, message, severity });
  };

  const handleAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const handleEdit = (row: TenantVegetationType) => {
    setEditing(row);
    setDialogOpen(true);
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id, {
      onSuccess: () => showSnack('Vegetation type deleted.', 'success'),
      onError: () => showSnack('Failed to delete vegetation type.', 'error'),
    });
  };

  const handleSubmit = (formData: {
    veg_class: string;
    max_fuel_load: number;
    accumulation_rate: number;
    model_class_name: string | null;
    model_subclass: number | null;
  }) => {
    if (editing) {
      updateMutation.mutate(
        { id: editing.id, ...formData },
        {
          onSuccess: () => {
            setDialogOpen(false);
            showSnack('Vegetation type updated.', 'success');
          },
          onError: () => showSnack('Failed to update vegetation type.', 'error'),
        }
      );
    } else {
      createMutation.mutate(formData, {
        onSuccess: () => {
          setDialogOpen(false);
          showSnack('Vegetation type added.', 'success');
        },
        onError: () => showSnack('Failed to add vegetation type.', 'error'),
      });
    }
  };

  const handleImport = () => {
    importMutation.mutate(undefined, {
      onSuccess: (result) => {
        const summary = (result as any)?.data;
        showSnack(
          `Import complete — ${summary?.created ?? 0} created, ${summary?.updated ?? 0} updated (${summary?.total ?? 0} total).`,
          'success'
        );
      },
      onError: () => showSnack('Import failed. Please try again.', 'error'),
    });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <ForestIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
        <Typography variant="h5" fontWeight="bold">
          Vegetation Management
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage tenant-level vegetation type defaults. These values are used to seed new projects.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search vegetation classes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 280 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {`${rows.length} of ${allRows.length} results`}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={handleImport}
          disabled={importMutation.isPending}
        >
          {importMutation.isPending ? 'Importing…' : 'Import'}
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add Veg Type
        </Button>
      </Box>

      <VegetationTypeTable
        rows={rows}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <VegetationTypeFormDialog
        open={dialogOpen}
        editing={editing}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        isSaving={isSaving}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
